import React, { useState } from 'react';
import { Box, IconButton, Drawer, List, ListItem, useMediaQuery, useTheme, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { Link } from 'gatsby';
import { useLocation } from '@reach/router';
import AlioLogo from '../assets/images/logo/Logo_DataDriven.png';
import AlioRegularLogo from '../assets/images/alioforlogo.png';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '../assets/images/majesticons/chat-2.svg';
import { useChat } from './chat';

// Custom AI Chat Icon Component
const AIChatIcon = ({ size = 28 }) => (
  <Box 
    sx={{ 
      position: 'relative', 
      width: size, 
      height: size,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Hover effects
      '&:hover': {
        transform: 'scale(1.1)',
        transition: 'all 0.3s ease',
      },
      // Active state for click feedback
      '&:active': {
        transform: 'scale(0.95)',
        transition: 'all 0.1s ease',
      },
    }}
  >
    <AnimatedLoader>
      <LoaderElement delay={0} />
      <LoaderElement delay={0.2} />
      <LoaderElement delay={0.4} />
    </AnimatedLoader>
  </Box>
);

const OriginalAIChatIcon = ({ size = 28 }) => (
  <Box 
    sx={{ 
      position: 'relative', 
      width: size, 
      height: size,
      cursor: 'pointer',
      // Pulse animation to draw attention
      animation: 'pulse 2s ease-in-out infinite',
      '@keyframes pulse': {
        '0%': {
          transform: 'scale(1)',
          filter: 'drop-shadow(0 0 0 rgba(255, 107, 53, 0))',
        },
        '50%': {
          transform: 'scale(1.05)',
          filter: 'drop-shadow(0 0 8px rgba(255, 107, 53, 0.6))',
        },
        '100%': {
          transform: 'scale(1)',
          filter: 'drop-shadow(0 0 0 rgba(255, 107, 53, 0))',
        },
      },
      // Hover effects
      '&:hover': {
        animation: 'none',
        transform: 'scale(1.1)',
        filter: 'drop-shadow(0 0 12px rgba(255, 107, 53, 0.8))',
        transition: 'all 0.3s ease',
      },
      // Active state for click feedback
      '&:active': {
        transform: 'scale(0.95)',
        transition: 'all 0.1s ease',
      },
    }}
  >
    <Box
      component="img"
      src={AlioRegularLogo}
      alt="Alio AI Agent"
      sx={{
        width: size,
        height: size,
        objectFit: 'contain',
        // Subtle glow effect
        filter: 'drop-shadow(0 0 4px rgba(255, 107, 53, 0.3))',
        transition: 'all 0.3s ease',
      }}
    />
  </Box>
);

const NavContainer = styled.div`
  width: 100%;
  height: 99px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background: rgba(5, 5, 5, 0.8);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    height: auto;
    min-height: 80px;
  }
`;

const NavContent = styled.div`
  width: 100%;
  max-width: 1440px;
  padding: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 1200px) {
    padding: 24px;
  }
  @media (max-width: 900px) {
    padding: 12px;
  }
  @media (max-width: 768px) {
    padding: 24px;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const LogoGroup = styled.div`
  display: flex;
  align-items: center;
  height: 37px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const AnimatedLoader = styled.div`
  --main-color: #ecf0f1;
  --point-color: #FF6B35;
  --size: 5px;
  
  display: flex;
  align-items: center;
  justify-content: center;
  gap: calc(var(--size) * 2);
`;

const LoaderElement = styled.div<{ delay: number }>`
  border-radius: 100%;
  border: var(--size) solid var(--point-color);
  width: var(--size);
  height: var(--size);
  animation: preloader 0.6s ease-in-out alternate ${props => props.delay}s infinite;

  @keyframes preloader {
    100% { 
      transform: scale(2); 
    }
  }
`;

const MenuGroup = styled.div`
  margin-left: 120px;
  display: flex;
  align-items: center;
  gap: 32px;
  @media (max-width: 1200px) {
    margin-left: 40px;
    gap: 24px;
  }
  @media (max-width: 900px) {
    margin-left: 10px;
    gap: 16px;
  }
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuGroup = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

const MenuItem = styled.div`
  padding: 8px 0;
  display: flex;
  align-items: center;
  color: white;
  font-size: 16px;
  font-family: 'Raleway', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  position: relative;
  height: 37px;

  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: 0;
    left: 0;
    background-color: white;
    transition: width 0.2s ease;
  }

  &:hover {
    opacity: 1;
    &:after {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    font-size: 14px;
    height: 32px;
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

const ContactButton = styled.div`
  padding: 10px 24px;
  margin-left: 10px;
  background: rgba(239, 170, 68, 0.2);
  border-radius: 35px;
  border: 1px solid rgba(239, 170, 68, 0.3);
  backdrop-filter: blur(5.85px);
  color: white;
  font-size: 16px;
  font-family: 'Raleway', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  height: 37px;
  display: flex;
  align-items: center;

  &:hover {
    background: rgba(239, 170, 68, 0.4);
    border-color: rgba(239, 170, 68, 0.5);
  }

  @media (max-width: 768px) {
    margin-left: 0;
    font-size: 14px;
    height: 32px;
  }
`;

const MobileDrawer = styled(Drawer)`
  .MuiDrawer-paper {
    background: rgba(5, 5, 5, 0.95);
    backdrop-filter: blur(10px);
    width: 280px;
    padding: 30px 0;
  }
`;

const MobileMenuItem = styled(ListItem)`
  padding: 16px 24px;
  color: white;
  font-family: 'Raleway', sans-serif;
  font-size: 18px;
  font-weight: 500;
  border-left: 3px solid transparent;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 170, 68, 0.1);
    border-left: 3px solid rgba(239, 170, 68, 0.5);
  }
`;

const MobileMenuHeader = styled.div`
  padding: 20px 24px;
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MobileContactButton = styled.div`
  margin: 20px 24px;
  padding: 12px 24px;
  background: rgba(239, 170, 68, 0.2);
  border-radius: 35px;
  border: 1px solid rgba(239, 170, 68, 0.3);
  backdrop-filter: blur(5.85px);
  color: white;
  font-size: 16px;
  font-family: 'Raleway', sans-serif;
  font-weight: 500;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(239, 170, 68, 0.4);
    border-color: rgba(239, 170, 68, 0.5);
  }
`;

const HamburgerIconButton = styled(IconButton)`
  color: #EFAA44;
  margin-right: -12px;
  
  &:hover {
    background-color: rgba(239, 170, 68, 0.1);
  }
`;

const Navigation = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // Make useChat conditional to prevent build errors
  let toggleChat = () => {};
  try {
    const { toggleChat: chatToggle } = useChat();
    toggleChat = chatToggle;
  } catch (error) {
    // ChatProvider not available, use empty function
    console.warn('ChatProvider not available in Navigation component');
  }

  const isCommunityPage = location.pathname === '/community' || location.pathname === '/community/';
  const isCompanyPage = location.pathname === '/company' || location.pathname === '/company/';
  const isBlogPage = location.pathname === '/blog' || location.pathname === '/blog/' || location.pathname.startsWith('/blog/');

  const getContactSectionId = () => {
    if (isCommunityPage) return 'contact-cv';
    if (isCompanyPage) return 'new-contact';
    return 'contact-section';
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isBlogPage && typeof window !== 'undefined') {
      window.location.href = '/#contact-section';
      return;
    }
    const sectionId = getContactSectionId();
    const contactSection = document.getElementById(sectionId);
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Menu items list
  const menuItems = [
    { label: 'Home', to: '/' },
    { label: 'Services', to: '/services' },
    { label: 'Expertise', to: '/expertise' },
    { label: 'Community', to: '/community' },
    { label: 'Careers', to: '/careers-opportunity' },
    { label: 'Company', to: '/company' }
  ];

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  const drawer = (
    <Box sx={{ width: '100%' }} role="presentation">
      <MobileMenuHeader>
        <Typography variant="h6" sx={{ fontFamily: 'Raleway', color: 'white', fontWeight: 600 }}>
          Menu
        </Typography>
        <IconButton onClick={handleDrawerToggle} sx={{ color: '#EFAA44' }}>
          <CloseIcon />
        </IconButton>
      </MobileMenuHeader>
      <List>
        {menuItems.map((item, index) => (
          <StyledLink key={index} to={item.to}>
            <MobileMenuItem 
              onClick={handleDrawerToggle}
              sx={{
                borderLeft: isActive(item.to) ? '3px solid #EFAA44' : '3px solid transparent',
                backgroundColor: isActive(item.to) ? 'rgba(239, 170, 68, 0.1)' : 'transparent',
              }}
            >
              {item.label}
            </MobileMenuItem>
          </StyledLink>
        ))}
      </List>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box
          onClick={() => {
            toggleChat();
            handleDrawerToggle();
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            color: '#FF6B35',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgba(255, 107, 53, 0.2)',
            },
          }}
        >
          <OriginalAIChatIcon />
          <Typography sx={{ fontFamily: 'Raleway', fontWeight: 500 }}>
            Chat with Alio Assistant
          </Typography>
        </Box>
        <Box component="a" href={`#${getContactSectionId()}`} onClick={handleContactClick} sx={{ textDecoration: 'none' }}>
          <MobileContactButton>
            Contact Us
          </MobileContactButton>
        </Box>
      </Box>
    </Box>
  );

  return (
    <NavContainer>
      <NavContent>
        <LogoGroup>
          <StyledLink to="/">
            <img src={AlioLogo} alt="Alio Logo" />
          </StyledLink>
        </LogoGroup>
        {isMobile ? (
          <MobileMenuGroup>
            <HamburgerIconButton 
              onClick={handleDrawerToggle}
              aria-label="open menu"
            >
              <MenuIcon />
            </HamburgerIconButton>
          </MobileMenuGroup>
        ) : (
          <MenuGroup>
            {menuItems.map((item, index) => (
              <StyledLink key={index} to={item.to}>
                <MenuItem>{item.label}</MenuItem>
              </StyledLink>
            ))}
            <IconButton
              onClick={toggleChat}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
              title="Chat with Alio Assistant"
            >
              <AIChatIcon />
            </IconButton>
            <Box 
              component="a" 
              href={`#${getContactSectionId()}`}
              onClick={handleContactClick}
              sx={{ textDecoration: 'none', color: 'inherit' }}
            >
              <ContactButton>Contact</ContactButton>
            </Box>
          </MenuGroup>
        )}
      </NavContent>
      <MobileDrawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true } as { keepMounted: boolean }}
      >
        {drawer}
      </MobileDrawer>
    </NavContainer>
  );
};

export default Navigation; 
